import {
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Progress,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react'
import ReviewCard from '@components/cards/review-card'
import ReviewForm from '@components/common/form/review-form'
import { useModalAction } from '@components/common/modal/modal.context'
import StarIcon from '@components/icons/star-icon'
import Pagination from '@components/ui/pagination'
import { ProductPageContext } from '@contexts/product-page'
import { useStore } from '@contexts/store'
import { useProductReviews } from '@framework/products/manage-product-reviews'
import { ROUTES } from '@utils/routes'
import { useContext, useState } from 'react'
import { GrNext, GrPrevious } from 'react-icons/gr'
import StarRatingComponent from 'react-star-rating-component'

const ProductReviewRating = () => {
  const { product } = useContext(ProductPageContext)
  const { storeData, user } = useStore()
  const countPerPage = 4
  const [filter, setFilter] = useState({
    page: 1,
    limit: countPerPage,
  })
  const { reviews } = product
  const {
    reviews: reviewsDetails,
    isLoading,
    mutate,
  } = useProductReviews(storeData?._id, product?._id, filter)

  const { openModal } = useModalAction()

  const maxRating = reviews?.rates
    ? Math.max(...Object.keys(reviews?.rates).map((key) => Number(key)))
    : 5

  return (
    <Flex
      gap={8}
      direction={{ base: 'column', md: 'row' }}
    >
      <Skeleton
        isLoaded={!isLoading}
        position="relative"
        flexDirection={'column'}
        display="flex"
        h={!isLoading ? 'auto' : '250px'}
        w={{ base: '100%', md: '35%', lg: '25%' }}
      >
        <Flex
          justify="center"
          align="center"
          bg="primary.500"
          borderRadius="8px"
          py={{ base: '20px', lg: '32px' }}
          w="full"
        >
          {reviews?.count ? (
            <HStack color="white" spacing="3">
              <Text
                as="span"
                fontWeight="600"
                fontSize={{ base: '40px', lg: '56px' }}
              >
                {Number(reviews?.average)?.toFixed(1)}
              </Text>
              <VStack spacing="1" align="start">
                <StarRatingComponent
                  name={`product-rating-average`}
                  starCount={maxRating}
                  value={reviews?.average}
                  starColor="#F5CA99"
                  editing={false}
                  emptyStarColor="#DFE6ED"
                  renderStarIcon={() => <StarIcon width="15px" height="15px" />}
                />
                <Text>
                  {reviews?.count} review{reviews?.count === 1 ? '' : 's'}
                </Text>
              </VStack>
            </HStack>
          ) : (
            <Text as="span" fontWeight="600" fontSize="20px" color="white">
              No reviews
            </Text>
          )}
        </Flex>
        <Text color="elegantBlack" fontSize="21px" fontWeight="600" mt="4">
          Rating breakdown
        </Text>
        <VStack spacing="1.5" w="full" mt="2">
          {Object.entries(reviews?.rates ?? {})
            ?.sort((a, b) => Number(b[0]) - Number(a[0]))
            ?.map((rate) => {
              const [star, count] = rate
              return (
                <Flex
                  key={star}
                  justify="space-between"
                  w="full"
                  align="center"
                >
                  <Text as="span">
                    {star} star{Number(star) === 1 ? '' : 's'}
                  </Text>
                  <Progress
                    value={count}
                    max={reviews?.count || 1}
                    borderRadius="full"
                    size="sm"
                    position="relative"
                    left={Number(star) === 1 ? '4px' : undefined}
                    colorScheme="primary"
                    w="60%"
                  />
                  <Text as="span" color="primary">
                    {count}
                  </Text>
                </Flex>
              )
            })}
        </VStack>
      </Skeleton>
      <Flex direction="column" w={{ base: '100%', md: '65%', lg: '75%' }}>
        <Box>
          <Flex gap="4" direction="column" w="full">
            {reviewsDetails?.data?.map((item: any, i: number) => (
              <ReviewCard item={item} key={`review-key-${item._id ?? i}`} />
            ))}
          </Flex>
          <Grid templateColumns={{ base: '1fr 1fr', md: '1fr 2fr 1fr' }}>
            <div></div>
            <div>
              {reviewsDetails?.data?.length ? (
                <Flex justify="center" mt="3.5" mb="10" align="center">
                  <Pagination
                    current={reviewsDetails?.currentPage ?? filter.page}
                    onChange={(page: number) => setFilter({ ...filter, page })}
                    pageSize={countPerPage}
                    total={reviewsDetails?.totalRecord}
                    prevIcon={
                      <GrPrevious size={12} style={{ color: '#090B17' }} />
                    }
                    nextIcon={<GrNext size={12} style={{ color: '#090B17' }} />}
                    className="order-table-pagination"
                  />
                </Flex>
              ) : null}
            </div>
            <Flex align="center" justify="end">
              {!user?.person?._id ? (
                <Button
                  rounded="full"
                  colorScheme="primary"
                  mb="10"
                  onClick={() => {
                    openModal('LOGIN_VIEW')
                  }}
                >
                  Write your review
                </Button>
              ) : null}
            </Flex>
          </Grid>
        </Box>
        {user?.person?._id ? (
          <ReviewForm mutateProductReviews={mutate} product={product} />
        ) : null}
      </Flex>
    </Flex>
  )
}

export default ProductReviewRating
